import React, { useEffect, useRef } from 'react';

/**
 * Slowly rotating spirograph pattern — matches the circular hypnotic decoration
 * from Breww's "under the hood" area. Rendered on <canvas> for cheap animation.
 * IntersectionObserver pauses the RAF when off-screen; ResizeObserver keeps it
 * sharp; DPR-aware for retina.
 *
 * Props:
 *   size            — pixel diameter (default 480)
 *   petals          — how many rotated ellipse layers to draw (default 42)
 *   strokeColor     — line color (rgba string)
 *   coreColor       — fill color of the central disc
 *   ringColor       — outer border ring color
 *   speed           — degrees per frame (default 0.08)
 *   dots            — number of orbiting dots (default 5)
 */
const SpirographCanvas = ({
  size = 480,
  petals = 42,
  strokeColor = 'rgba(59,130,246,0.30)',
  coreColor = 'rgba(255,255,255,0.95)',
  ringColor = 'rgba(16,185,129,0.55)',
  speed = 0.08,
  dots = 5,
  className = '',
}) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const angleRef = useRef(0);
  const activeRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cx = size / 2;
    const cy = size / 2;
    const outer = size * 0.46;
    const inner = size * 0.16;
    const petalRx = size * 0.36;
    const petalRy = size * 0.16;

    const draw = () => {
      ctx.clearRect(0, 0, size, size);

      // Outer ring
      ctx.beginPath();
      ctx.arc(cx, cy, outer, 0, Math.PI * 2);
      ctx.strokeStyle = ringColor;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Petal ellipses rotated around the center
      const base = (angleRef.current * Math.PI) / 180;
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 0.6;
      for (let i = 0; i < petals; i++) {
        const a = base + (Math.PI * 2 * i) / petals;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(a);
        ctx.beginPath();
        ctx.ellipse(0, 0, petalRx, petalRy, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      // Core disc
      ctx.beginPath();
      ctx.arc(cx, cy, inner, 0, Math.PI * 2);
      ctx.fillStyle = coreColor;
      ctx.fill();
      ctx.strokeStyle = 'rgba(148,163,184,0.35)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Orbiting dots
      for (let i = 0; i < dots; i++) {
        const t = base * (1 + i * 0.15) + i * 1.7;
        const r = outer * (0.42 + (i % 3) * 0.12);
        const dx = cx + Math.cos(t) * r;
        const dy = cy + Math.sin(t) * r;
        ctx.beginPath();
        ctx.arc(dx, dy, 3.2, 0, Math.PI * 2);
        ctx.fillStyle = i % 2 === 0 ? 'rgba(16,185,129,0.85)' : 'rgba(59,130,246,0.85)';
        ctx.fill();
      }
    };

    const tick = () => {
      if (activeRef.current) {
        angleRef.current = (angleRef.current + speed) % 360;
        draw();
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      ([e]) => { activeRef.current = e.isIntersecting; },
      { threshold: 0.01 }
    );
    io.observe(canvas);

    draw();
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      io.disconnect();
    };
  }, [size, petals, strokeColor, coreColor, ringColor, speed, dots]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
};

export default SpirographCanvas;
